import { useState } from "react"
import {
  Container,
  Stack,
  Grid,
  Typography,
  Alert,
  Collapse,
  Button,
  Box,
  Slider,
} from "@mui/material"
import { DatePicker } from "@mui/x-date-pickers"
import { FilterList } from "@mui/icons-material"
import { BotSelector, ChatList, MessageViewer } from "../components"
import { useGetChatsByBotQuery } from "../store"
import { useViewedChats } from "../hooks"

const MSG_MIN = 0
const MSG_MAX = 1000

const toRfc3339Start = (date: Date | null): string | undefined => {
  if (!date) return undefined
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, "0")
  const d = String(date.getDate()).padStart(2, "0")
  return `${y}-${m}-${d}T00:00:00Z`
}

const toRfc3339End = (date: Date | null): string | undefined => {
  if (!date) return undefined
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, "0")
  const d = String(date.getDate()).padStart(2, "0")
  return `${y}-${m}-${d}T23:59:59Z`
}

export const ChatViewerPage: React.FC = () => {
  const { viewedChats, markChatViewed, getNewMessagesCount } = useViewedChats()

  const [selectedBotUsername, setSelectedBotUsername] = useState<string | null>(
    null,
  )
  const [selectedChatId, setSelectedChatId] = useState<string | null>(null)
  const [filtersOpen, setFiltersOpen] = useState(false)

  const [dateFrom, setDateFrom] = useState<Date | null>(null)
  const [dateTo, setDateTo] = useState<Date | null>(null)
  const [messagesRange, setMessagesRange] = useState<[number, number]>([
    MSG_MIN,
    MSG_MAX,
  ])

  const hasActiveFilters =
    dateFrom !== null ||
    dateTo !== null ||
    messagesRange[0] !== MSG_MIN ||
    messagesRange[1] !== MSG_MAX

  const queryParams = {
    bot_username: selectedBotUsername!,
    ...(toRfc3339Start(dateFrom)
      ? { chat_created_from: toRfc3339Start(dateFrom) }
      : {}),
    ...(toRfc3339End(dateTo) ? { chat_created_to: toRfc3339End(dateTo) } : {}),
    ...(messagesRange[0] !== MSG_MIN ? { min_messages: messagesRange[0] } : {}),
    ...(messagesRange[1] !== MSG_MAX ? { max_messages: messagesRange[1] } : {}),
  }

  const {
    data: chats,
    isFetching,
    error,
  } = useGetChatsByBotQuery({ queryParams }, { skip: !selectedBotUsername })

  const selectedChat = chats?.find((chat) => chat.chat_id === selectedChatId)

  const handleBotSelect = (botUsername: string | null) => {
    setSelectedBotUsername(botUsername)
    setSelectedChatId(null)
  }

  const handleChatSelect = (chatId: string) => {
    setSelectedChatId(chatId)
    const chat = chats?.find((c) => c.chat_id === chatId)
    if (chat) {
      markChatViewed(chatId, chat.messages.length)
    }
  }

  const handleResetFilters = () => {
    setDateFrom(null)
    setDateTo(null)
    setMessagesRange([MSG_MIN, MSG_MAX])
    setSelectedChatId(null)
  }

  const handleMessagesRangeChange = (_: Event, value: number | number[]) => {
    setMessagesRange(value as [number, number])
    setSelectedChatId(null)
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Stack spacing={3}>
        {/* Заголовок */}
        <Typography variant="h4" component="h1">
          Просмотр чатов Telegram
        </Typography>

        {/* Селекторы типа бота и бота */}
        <BotSelector onBotSelect={handleBotSelect} />

        {/* Кнопка показа фильтров */}
        {selectedBotUsername && (
          <Box>
            <Button
              startIcon={<FilterList />}
              variant={hasActiveFilters ? "contained" : "outlined"}
              size="small"
              onClick={() => setFiltersOpen((o) => !o)}
            >
              Фильтры{hasActiveFilters ? " (активны)" : ""}
            </Button>

            <Collapse in={filtersOpen} unmountOnExit>
              <Stack spacing={3} sx={{ mt: 2, maxWidth: 700 }}>
                {/* Даты */}
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <DatePicker
                    label="Дата создания от"
                    value={dateFrom}
                    onChange={(val) => {
                      setDateFrom(val)
                      setSelectedChatId(null)
                    }}
                    maxDate={dateTo ?? undefined}
                    slotProps={{ textField: { size: "small" } }}
                  />
                  <DatePicker
                    label="Дата создания до"
                    value={dateTo}
                    onChange={(val) => {
                      setDateTo(val)
                      setSelectedChatId(null)
                    }}
                    minDate={dateFrom ?? undefined}
                    slotProps={{ textField: { size: "small" } }}
                  />
                </Stack>

                {/* Ползунок кол-ва сообщений */}
                <Box>
                  <Typography variant="body2" gutterBottom>
                    Количество сообщений: {messagesRange[0]} —{" "}
                    {messagesRange[1]}
                    {messagesRange[1] === MSG_MAX ? "+" : ""}
                  </Typography>
                  <Slider
                    value={messagesRange}
                    onChange={handleMessagesRangeChange}
                    min={MSG_MIN}
                    max={MSG_MAX}
                    step={10}
                    valueLabelDisplay="auto"
                    disableSwap
                  />
                </Box>

                {hasActiveFilters && (
                  <Box>
                    <Button
                      variant="text"
                      size="small"
                      onClick={handleResetFilters}
                    >
                      Сбросить фильтры
                    </Button>
                  </Box>
                )}
              </Stack>
            </Collapse>
          </Box>
        )}

        {/* Ошибка загрузки */}
        {!!error && (
          <Alert severity="error">
            Ошибка загрузки чатов. Попробуйте выбрать другого бота.
          </Alert>
        )}

        {/* Основная область с чатами и сообщениями */}
        {selectedBotUsername && (
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <ChatList
                chats={chats ?? []}
                selectedChatId={selectedChatId}
                onChatSelect={handleChatSelect}
                loading={isFetching}
                viewedChats={viewedChats}
                getNewMessagesCount={getNewMessagesCount}
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <MessageViewer chat={selectedChat ?? null} loading={isFetching} />
            </Grid>
          </Grid>
        )}
      </Stack>
    </Container>
  )
}
